import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  ArrowLeft,
  Users,
  BarChart3,
  Music,
  Upload,
  Trash2,
  Shield,
  TrendingUp,
  CheckCircle,
  Clock,
  Loader2,
  ChevronDown,
  Eye
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const chakraOptions = [
  { value: "root", label: "Root (Muladhara)" },
  { value: "sacral", label: "Sacral (Svadhisthana)" },
  { value: "solar_plexus", label: "Solar Plexus (Manipura)" },
  { value: "heart", label: "Heart (Anahata)" },
  { value: "throat", label: "Throat (Vishuddha)" },
  { value: "third_eye", label: "Third Eye (Ajna)" },
  { value: "crown", label: "Crown (Sahasrara)" },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [audioFiles, setAudioFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const fileInputRef = useRef(null);

  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    chakra: "",
    title: "",
    description: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, metricsRes, audioRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/metrics`),
        axios.get(`${API}/audio`)
      ]);
      setUsers(usersRes.data);
      setMetrics(metricsRes.data);
      setAudioFiles(audioRes.data);
    } catch (error) {
      console.error("Error fetching admin data:", error);
      if (error.response?.status === 403) {
        toast.error("Admin access required");
        navigate('/dashboard');
      } else {
        toast.error("Failed to load admin data");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!uploadForm.chakra || !uploadForm.title) {
      toast.error("Please fill in chakra and title before uploading");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      await axios.post(
        `${API}/admin/audio?chakra=${uploadForm.chakra}&title=${encodeURIComponent(uploadForm.title)}&description=${encodeURIComponent(uploadForm.description)}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success("Audio uploaded successfully!");
      setUploadForm({ chakra: "", title: "", description: "" });
      fetchData();
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload audio");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDeleteAudio = async (audioId) => {
    if (!window.confirm("Are you sure you want to delete this audio file?")) return;

    try {
      await axios.delete(`${API}/admin/audio/${audioId}`);
      toast.success("Audio deleted successfully");
      fetchData();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete audio");
    }
  };

  const handleMakeAdmin = async (email) => {
    try {
      await axios.post(`${API}/admin/make-admin/${email}`);
      toast.success(`${email} is now an admin`);
      fetchData();
    } catch (error) {
      console.error("Make admin error:", error);
      toast.error("Failed to promote user to admin");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cosmic-bg-primary flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-cosmic-accent-gold/30 border-t-cosmic-accent-gold rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cosmic-bg-primary p-6" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
            data-testid="admin-back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-cosmic-text-secondary" />
          </button>
          <div>
            <h1 className="font-heading text-3xl text-cosmic-text-primary flex items-center gap-3">
              <Shield className="w-8 h-8 text-cosmic-accent-gold" />
              Admin Dashboard
            </h1>
            <p className="text-cosmic-text-muted">Manage users, content, and track conversions</p>
          </div>
        </div>
        <Button onClick={logout} variant="ghost" className="text-cosmic-text-muted">
          Logout
        </Button>
      </div>

      {/* Metrics Cards */}
      {metrics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <motion.div 
            className="glass-card p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="metric-total-users"
          >
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-cosmic-accent-violet" />
              <span className="text-cosmic-text-muted text-sm">Total Users</span>
            </div>
            <p className="font-heading text-3xl text-cosmic-text-primary">{metrics.total_users}</p>
          </motion.div>

          <motion.div 
            className="glass-card p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            data-testid="metric-completed"
          >
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle className="w-5 h-5 text-status-success" />
              <span className="text-cosmic-text-muted text-sm">Completed Assessments</span>
            </div>
            <p className="font-heading text-3xl text-cosmic-text-primary">{metrics.completed_assessments}</p>
          </motion.div>

          <motion.div 
            className="glass-card p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            data-testid="metric-rate"
          >
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="w-5 h-5 text-cosmic-accent-gold" />
              <span className="text-cosmic-text-muted text-sm">Completion Rate</span>
            </div>
            <p className="font-heading text-3xl text-cosmic-text-primary">{metrics.completion_rate}%</p>
          </motion.div>

          <motion.div 
            className="glass-card p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            data-testid="metric-checkins"
          >
            <div className="flex items-center gap-3 mb-2">
              <Clock className="w-5 h-5 text-chakra-throat" />
              <span className="text-cosmic-text-muted text-sm">Total Check-ins</span>
            </div>
            <p className="font-heading text-3xl text-cosmic-text-primary">{metrics.total_checkins}</p>
          </motion.div>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-cosmic-bg-secondary border border-white/10 mb-6">
          <TabsTrigger 
            value="users" 
            className="data-[state=active]:bg-cosmic-accent-gold/20 data-[state=active]:text-cosmic-accent-gold"
            data-testid="tab-users"
          >
            <Users className="w-4 h-4 mr-2" />
            Users
          </TabsTrigger>
          <TabsTrigger 
            value="audio"
            className="data-[state=active]:bg-cosmic-accent-gold/20 data-[state=active]:text-cosmic-accent-gold"
            data-testid="tab-audio"
          >
            <Music className="w-4 h-4 mr-2" />
            Audio
          </TabsTrigger>
          <TabsTrigger 
            value="metrics"
            className="data-[state=active]:bg-cosmic-accent-gold/20 data-[state=active]:text-cosmic-accent-gold"
            data-testid="tab-metrics"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Metrics
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users">
          <div className="glass-card overflow-hidden">
            <ScrollArea className="h-[600px]">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-cosmic-text-muted">User</TableHead>
                    <TableHead className="text-cosmic-text-muted">Role</TableHead>
                    <TableHead className="text-cosmic-text-muted">Assessment</TableHead>
                    <TableHead className="text-cosmic-text-muted">Check-ins</TableHead>
                    <TableHead className="text-cosmic-text-muted">Joined</TableHead>
                    <TableHead className="text-cosmic-text-muted">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.user_id} className="border-white/5 hover:bg-white/5">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {u.picture ? (
                            <img src={u.picture} alt="" className="w-8 h-8 rounded-full" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-cosmic-accent-violet flex items-center justify-center text-white text-sm">
                              {u.name?.[0] || 'U'}
                            </div>
                          )}
                          <div>
                            <p className="text-cosmic-text-primary">{u.name}</p>
                            <p className="text-xs text-cosmic-text-muted">{u.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          u.role === 'admin' 
                            ? 'bg-cosmic-accent-gold/20 text-cosmic-accent-gold' 
                            : 'bg-white/10 text-cosmic-text-muted'
                        }`}>
                          {u.role}
                        </span>
                      </TableCell>
                      <TableCell>
                        {u.has_completed_assessment ? (
                          <span className="text-status-success flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" /> Completed
                          </span>
                        ) : (
                          <span className="text-cosmic-text-muted">Pending</span>
                        )}
                      </TableCell>
                      <TableCell className="text-cosmic-text-secondary">
                        {u.checkin_count || 0}
                      </TableCell>
                      <TableCell className="text-cosmic-text-muted text-sm">
                        {new Date(u.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-cosmic-text-muted">
                              Actions <ChevronDown className="w-4 h-4 ml-1" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="bg-cosmic-bg-secondary border-white/10">
                            {u.assessment && (
                              <DropdownMenuItem 
                                onClick={() => setSelectedUser(u)}
                                className="text-cosmic-text-secondary hover:text-cosmic-text-primary cursor-pointer"
                              >
                                <Eye className="w-4 h-4 mr-2" /> View Assessment
                              </DropdownMenuItem>
                            )}
                            {u.role !== 'admin' && (
                              <DropdownMenuItem 
                                onClick={() => handleMakeAdmin(u.email)}
                                className="text-cosmic-text-secondary hover:text-cosmic-text-primary cursor-pointer"
                              >
                                <Shield className="w-4 h-4 mr-2" /> Make Admin
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          </div>

          {/* User Assessment Modal */}
          {selectedUser && selectedUser.assessment && (
            <div 
              className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6"
              onClick={() => setSelectedUser(null)}
            >
              <motion.div 
                className="glass-card max-w-2xl w-full max-h-[80vh] overflow-auto p-6"
                onClick={e => e.stopPropagation()}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-heading text-2xl text-cosmic-text-primary">
                    {selectedUser.name}'s Assessment
                  </h3>
                  <Button 
                    variant="ghost" 
                    onClick={() => setSelectedUser(null)}
                    className="text-cosmic-text-muted"
                  >
                    Close
                  </Button>
                </div>

                <div className="space-y-4">
                  <h4 className="text-cosmic-text-secondary font-medium">Chakra Analysis</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {selectedUser.assessment.chakra_analysis?.map((chakra, i) => (
                      <div key={i} className="bg-white/5 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-cosmic-text-primary">{chakra.name}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            chakra.status === 'balanced' ? 'bg-status-success/20 text-status-success' :
                            chakra.status === 'underactive' ? 'bg-status-warning/20 text-status-warning' :
                            'bg-status-error/20 text-status-error'
                          }`}>
                            {chakra.status}
                          </span>
                        </div>
                        <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-cosmic-accent-gold"
                            style={{ width: `${chakra.score}%` }}
                          />
                        </div>
                        <span className="text-xs text-cosmic-text-muted mt-1 block">
                          Score: {chakra.score}%
                        </span>
                      </div>
                    ))}
                  </div>

                  {selectedUser.assessment.ai_insights && (
                    <div className="mt-6">
                      <h4 className="text-cosmic-text-secondary font-medium mb-2">AI Insights</h4>
                      <p className="text-cosmic-text-muted text-sm bg-white/5 rounded-lg p-4">
                        {selectedUser.assessment.ai_insights}
                      </p>
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
          )}
        </TabsContent>

        {/* Audio Tab */}
        <TabsContent value="audio">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Upload Form */}
            <div className="glass-card p-6" data-testid="audio-upload-form">
              <h3 className="font-heading text-xl text-cosmic-text-primary mb-4 flex items-center gap-2">
                <Upload className="w-5 h-5 text-cosmic-accent-gold" />
                Upload Audio
              </h3>

              <div className="space-y-4">
                <div>
                  <Label className="text-cosmic-text-secondary mb-2 block">Chakra</Label>
                  <Select
                    value={uploadForm.chakra}
                    onValueChange={(value) => setUploadForm(prev => ({ ...prev, chakra: value }))}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10 text-cosmic-text-primary">
                      <SelectValue placeholder="Select chakra" />
                    </SelectTrigger>
                    <SelectContent className="bg-cosmic-bg-secondary border-white/10">
                      {chakraOptions.map(opt => (
                        <SelectItem 
                          key={opt.value} 
                          value={opt.value}
                          className="text-cosmic-text-secondary hover:text-cosmic-text-primary"
                        >
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-cosmic-text-secondary mb-2 block">Title</Label>
                  <Input
                    value={uploadForm.title}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Morning Root Grounding"
                    className="cosmic-input"
                  />
                </div>

                <div>
                  <Label className="text-cosmic-text-secondary mb-2 block">Description (optional)</Label>
                  <Textarea
                    value={uploadForm.description}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description of the meditation..."
                    className="cosmic-input min-h-[80px]"
                  />
                </div>

                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".mp3,.wav,.m4a,.ogg"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="audio-upload"
                  />
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={!uploadForm.chakra || !uploadForm.title || uploading}
                    className="w-full btn-gold"
                    data-testid="upload-audio-btn"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-5 h-5 mr-2" />
                        Select Audio File
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Audio List */}
            <div className="lg:col-span-2 glass-card p-6">
              <h3 className="font-heading text-xl text-cosmic-text-primary mb-4">
                Uploaded Audio ({audioFiles.length})
              </h3>

              {audioFiles.length === 0 ? (
                <p className="text-cosmic-text-muted text-center py-8">
                  No audio files uploaded yet. Upload your first guided meditation!
                </p>
              ) : (
                <div className="space-y-3">
                  {audioFiles.map(audio => (
                    <div 
                      key={audio.audio_id}
                      className="flex items-center justify-between bg-white/5 rounded-lg p-4"
                      data-testid={`audio-item-${audio.audio_id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-cosmic-accent-gold/20 flex items-center justify-center">
                          <Music className="w-5 h-5 text-cosmic-accent-gold" />
                        </div>
                        <div>
                          <p className="text-cosmic-text-primary font-medium">{audio.title}</p>
                          <p className="text-xs text-cosmic-text-muted">
                            {audio.chakra} • {new Date(audio.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteAudio(audio.audio_id)}
                        className="text-status-error hover:text-status-error hover:bg-status-error/10"
                        data-testid={`delete-audio-${audio.audio_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Metrics Tab */}
        <TabsContent value="metrics">
          {metrics && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Conversion Events */}
              <div className="glass-card p-6">
                <h3 className="font-heading text-xl text-cosmic-text-primary mb-4">
                  Conversion Events
                </h3>
                <div className="space-y-3">
                  {Object.entries(metrics.conversion_events || {}).map(([event, count]) => (
                    <div key={event} className="flex items-center justify-between bg-white/5 rounded-lg p-4">
                      <span className="text-cosmic-text-secondary capitalize">
                        {event.replace(/_/g, ' ')}
                      </span>
                      <span className="font-heading text-xl text-cosmic-accent-gold">{count}</span>
                    </div>
                  ))}
                  {Object.keys(metrics.conversion_events || {}).length === 0 && (
                    <p className="text-cosmic-text-muted text-center py-4">No conversion events yet</p>
                  )}
                </div>
              </div>

              {/* Daily Signups */}
              <div className="glass-card p-6">
                <h3 className="font-heading text-xl text-cosmic-text-primary mb-4">
                  Daily Signups (Last 7 Days)
                </h3>
                <div className="space-y-2">
                  {metrics.daily_signups?.map((day, i) => (
                    <div key={day.date} className="flex items-center gap-4">
                      <span className="text-cosmic-text-muted text-sm w-24">
                        {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                      </span>
                      <div className="flex-1 h-6 bg-white/5 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-cosmic-accent-violet/60 rounded-full transition-all"
                          style={{ 
                            width: `${Math.min(100, (day.count / Math.max(1, ...metrics.daily_signups.map(d => d.count))) * 100)}%` 
                          }}
                        />
                      </div>
                      <span className="text-cosmic-text-primary font-medium w-8 text-right">{day.count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Summary Stats */}
              <div className="lg:col-span-2 glass-card p-6">
                <h3 className="font-heading text-xl text-cosmic-text-primary mb-4">
                  Platform Summary
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white/5 rounded-lg p-4 text-center">
                    <p className="text-cosmic-text-muted text-sm mb-1">Total Audio Files</p>
                    <p className="font-heading text-2xl text-cosmic-text-primary">{metrics.total_audio_files}</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-4 text-center">
                    <p className="text-cosmic-text-muted text-sm mb-1">Avg Check-ins/User</p>
                    <p className="font-heading text-2xl text-cosmic-text-primary">
                      {metrics.total_users > 0 
                        ? (metrics.total_checkins / metrics.total_users).toFixed(1) 
                        : '0'}
                    </p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-4 text-center">
                    <p className="text-cosmic-text-muted text-sm mb-1">Engagement Rate</p>
                    <p className="font-heading text-2xl text-cosmic-text-primary">
                      {metrics.completed_assessments > 0 
                        ? ((metrics.total_checkins / metrics.completed_assessments) * 100).toFixed(0) 
                        : '0'}%
                    </p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-4 text-center">
                    <p className="text-cosmic-text-muted text-sm mb-1">Upgrade Clicks</p>
                    <p className="font-heading text-2xl text-cosmic-accent-gold">
                      {metrics.conversion_events?.clicked_upgrade || 0}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
